import { Children, createContext, isValidElement, useContext } from 'react';
import type { ReactNode } from 'react';
import { cx } from './cx';
import { Badge } from './Badge';

export type StatLayout = 'tile' | 'row' | 'auto';

export interface StatTileProps {
  label: ReactNode;
  /** Pre-formatted. Money arrives here already as 'R 2 260 000,00', not cents. */
  value: ReactNode;
  /** Qualifier under the value: 'of R 1 200 000,00 awarded', 'across 4 funders'. */
  sub?: ReactNode;
  /** Signed change. direction says whether up is good, which is not always. */
  delta?: { value: string; direction: 'up' | 'down' | 'flat'; good?: boolean };
  /** Series for a bare trend line. Six to twenty points reads best. Not drawn in the row layout. */
  sparkline?: number[];
  /** Right-hand adornment, usually a GrantHealthGlyph or a Badge. */
  aside?: ReactNode;
  /**
   * `tile`, the white panel, is the default on its own. `row` is a register
   * row: label left in the sans, figure right in the record face at 17px.
   * `auto` is a row below 640px and a tile above, and is what StatGroup asks
   * its tiles for.
   */
  layout?: StatLayout;
  className?: string;
}

const GroupLayout = createContext<StatLayout | null>(null);

/*
 * The classes for each part, per layout. `auto` is the row recipe with the
 * tile recipe behind `sm:`, so one element serves both and nothing is
 * rendered twice for a screen reader to find.
 */
// Tailwind finds class names by reading the source, so every one of these
// is written out whole; none is assembled from pieces.
const parts = {
  root: {
    tile: 'w-full [container-type:inline-size] grid grid-cols-[minmax(0,1fr)_auto] content-start gap-x-3 rounded-ct-md border border-line bg-paper-card px-4 py-4 shadow-card',
    row: 'grid min-h-[52px] grid-cols-[minmax(0,1fr)_auto_auto] content-center items-center gap-x-3 px-4 py-3',
    auto:
      'grid min-h-[52px] grid-cols-[minmax(0,1fr)_auto_auto] content-center items-center gap-x-3 px-4 py-3 ' +
      'sm:w-full sm:[container-type:inline-size] sm:min-h-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:content-start sm:items-stretch sm:rounded-ct-md sm:border sm:border-line sm:bg-paper-card sm:py-4 sm:shadow-card',
  },
  label: {
    tile: 'col-start-1 row-start-1 text-xs font-semibold text-ink-3',
    row: 'col-start-1 row-start-1 text-sm text-ink',
    auto: 'col-start-1 row-start-1 text-sm text-ink sm:text-xs sm:font-semibold sm:text-ink-3',
  },
  figure: {
    tile: 'col-start-1 row-start-2 mt-3 whitespace-nowrap text-[length:clamp(0.9375rem,10.5cqi,1.8rem)] font-medium leading-[1.05] tracking-[-0.035em] text-ink',
    row: 'col-start-2 row-start-1 row-span-2 self-center whitespace-nowrap text-right text-[17px] font-medium leading-6 tracking-normal text-ink',
    auto:
      'col-start-2 row-start-1 row-span-2 self-center whitespace-nowrap text-right text-[17px] font-medium leading-6 tracking-normal text-ink ' +
      'sm:col-start-1 sm:row-start-2 sm:row-span-1 sm:self-auto sm:mt-3 sm:text-left sm:text-[length:clamp(0.9375rem,10.5cqi,1.8rem)] sm:leading-[1.05] sm:tracking-[-0.035em]',
  },
  // A value that is words ("Not set", "Not yet counted") is not a figure:
  // it is set in the sans, may wrap, and is not shouted at display size.
  words: {
    tile: 'col-start-1 row-start-2 mt-3 text-lg font-semibold leading-tight text-ink',
    row: 'col-start-2 row-start-1 row-span-2 self-center text-right text-sm font-semibold text-ink',
    auto:
      'col-start-2 row-start-1 row-span-2 self-center text-right text-sm font-semibold text-ink ' +
      'sm:col-start-1 sm:row-start-2 sm:row-span-1 sm:self-auto sm:mt-3 sm:text-left sm:text-lg sm:leading-tight',
  },
  sub: {
    tile: 'col-start-1 row-start-3 mt-3 text-xs text-ink-2',
    row: 'col-start-1 row-start-2 text-xs text-ink-3',
    auto: 'col-start-1 row-start-2 text-xs text-ink-3 sm:row-start-3 sm:mt-3 sm:text-ink-2',
  },
  aside: {
    tile: 'col-start-2 row-start-1 row-span-3 self-start',
    row: 'col-start-3 row-start-1 row-span-2 self-center',
    auto: 'col-start-3 row-start-1 row-span-2 self-center sm:col-start-2 sm:row-span-3 sm:self-start',
  },
  trend: {
    tile: 'col-span-2 col-start-1 row-start-4 mt-3 flex items-end justify-between gap-3',
    row: 'col-start-1 row-start-3 mt-1 flex items-center gap-3',
    auto: 'col-start-1 row-start-3 mt-1 flex items-center gap-3 sm:col-span-2 sm:col-start-1 sm:row-start-4 sm:mt-3 sm:items-end sm:justify-between',
  },
  spark: { tile: '', row: 'hidden', auto: 'hidden sm:block' },
} as const;

/** True for a value that is a sentence fragment rather than a figure. */
function isWords(value: ReactNode): boolean {
  return typeof value === 'string' && /[A-Za-z]{3,}/.test(value.replace(/^R\s/, ''));
}

/**
 * The one recipe for a headline figure: a label, a large JetBrains Mono
 * figure, a qualifier.
 *
 * The figure never splits. It used to carry `overflow-wrap: break-word`
 * with a viewport-sized clamp, which broke `R 1 250 000,00` into
 * "R 1 250 000,0" over "0" in a two-up grid at 360px and again in the
 * four-up row beside the rail at 1280px: a different number, on the one
 * element whose only job is the number. It is `nowrap` now and sized from
 * the tile's own width (container units), so a narrow tile gets a smaller
 * figure instead of a broken one; on a phone, inside a StatGroup, the tile
 * becomes a register row where the figure has the whole right-hand side.
 *
 * The label was uppercase mono at 0.09em of tracking. That eyebrow is
 * retired by name in docs/DESIGN-DIRECTION.md: mono is the record face
 * and carries the record, not the furniture.
 */
export function StatTile({ label, value, sub, delta, sparkline, aside, layout, className }: StatTileProps) {
  const group = useContext(GroupLayout);
  const l: StatLayout = layout ?? group ?? 'tile';
  const deltaGood = delta?.good ?? delta?.direction === 'up';
  const deltaTone = delta ? (delta.direction === 'flat' ? 'neutral' : deltaGood ? 'success' : 'danger') : 'neutral';
  const arrow = delta ? (delta.direction === 'up' ? '↑' : delta.direction === 'down' ? '↓' : '→') : '';
  const words = isWords(value);
  return (
    <div className={cx(parts.root[l], className)}>
      <p className={parts.label[l]}>{label}</p>
      <p className={cx(words ? parts.words[l] : cx('record', parts.figure[l]))}>{value}</p>
      {sub ? <p className={parts.sub[l]}>{sub}</p> : null}
      {aside ? <div className={parts.aside[l]}>{aside}</div> : null}
      {delta || sparkline ? (
        <div className={parts.trend[l]}>
          {delta ? (
            // An arrow plus the sign inside the stamp, because colour alone
            // cannot carry the direction for a colour-blind reader.
            <Badge tone={deltaTone} size="sm">
              <span aria-hidden="true">{arrow}</span> {delta.value}
            </Badge>
          ) : (
            <span />
          )}
          {sparkline && sparkline.length > 1 ? <Sparkline points={sparkline} className={parts.spark[l]} /> : null}
        </div>
      ) : null}
    </div>
  );
}

export interface StatGroupProps {
  /** StatTiles. They take the group's layout unless one sets its own. */
  children: ReactNode;
  /** Names the group for a screen reader: "Money", "This call's envelope". */
  label?: string;
  className?: string;
}

/**
 * A set of headline figures.
 *
 * Below 640px it is one white panel holding a register, a row per figure,
 * 52px each: label left in the sans, figure right in the record face,
 * qualifier under the label. Four tiles in a two-up grid used to take a
 * whole phone screen and split their figures doing it.
 *
 * Above 640px it is the tile grid, and the number of columns comes from the
 * group's own width rather than the viewport's: four across only when each
 * tile has room for a full Rand figure, otherwise two (the odd one out of
 * three spans both). So the same group is right beside a 224px rail, in a
 * card, or across a full desk.
 */
export function StatGroup({ children, label, className }: StatGroupProps) {
  const count = Children.toArray(children).filter(isValidElement).length;
  return (
    <GroupLayout.Provider value="auto">
      <div role={label ? 'group' : undefined} aria-label={label} className={cx('ct-stat-group', className)}>
        <div className="ct-stat-grid" data-count={Math.min(count, 5)}>
          {children}
        </div>
      </div>
    </GroupLayout.Provider>
  );
}

function Sparkline({ points, width = 76, height = 22, className }: { points: number[]; width?: number; height?: number; className?: string }) {
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const pad = 2;
  const step = (width - pad * 2) / (points.length - 1);
  const xy = points.map((p, i) => [pad + i * step, pad + (height - pad * 2) - ((p - min) / span) * (height - pad * 2)]);
  const d = xy.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const [lx, ly] = xy[xy.length - 1];
  return (
    // Decorative: the figure above already states the number. A 1.5px ink
    // line with the last point marked by a square.
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" className={cx('shrink-0 text-ink', className)}>
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter" />
      <rect x={lx - 2} y={ly - 2} width="4" height="4" fill="currentColor" />
    </svg>
  );
}
